export default () => ({
  menuOpen: false,
  isAtTop: true,
  isHomePage: false, // Will be set from Liquid
  isLoaded: false,

  init() {
    this.isHomePage = this.$el.dataset.isHomePage === "true";

    if (this.isHomePage) {
      // Detect SPA navigation (e.g. Nosto pushState) away from home page
      const homePagePath = window.location.pathname;
      const checkUrl = () => {
        const nextIsHome = window.location.pathname === homePagePath;
        if (nextIsHome === this.isHomePage) return;

        // When leaving home, strip the width/transform transition classes
        // and pause the SVG's CSS transition BEFORE flipping state, so the
        // resulting class swap snaps instead of animating.
        if (!nextIsHome) {
          const wrapper = document.getElementById("logo-wrapper");
          if (wrapper) {
            wrapper.classList.remove(
              "header-logo-wrapper-transitions-down",
              "header-logo-wrapper-transitions-up"
            );
          }
          const svg = document.getElementById("hero-logo-svg");
          if (svg) {
            svg.style.transition = "none";
            requestAnimationFrame(() => {
              svg.style.transition = "";
            });
          }
        }

        this.isHomePage = nextIsHome;
      };
      const originalPushState = history.pushState.bind(history);
      history.pushState = (...args) => {
        originalPushState(...args);
        checkUrl();
      };
      window.addEventListener("popstate", checkUrl);
    }

    // Sync DOM state when isHomePage changes via SPA navigation.
    // The header is rendered server-side by Liquid, so leftover static
    // classes from the original (home) render must be reconciled here.
    this.$watch("isHomePage", (value) => {
      const logoWrapper = document.getElementById("logo-wrapper");
      if (logoWrapper) {
        if (value) {
          logoWrapper.classList.remove("translate-y-[2px]");
          logoWrapper.classList.add(
            "w-full",
            "header-logo-wrapper-transitions-down"
          );
        } else {
          logoWrapper.classList.remove(
            "w-full",
            "header-logo-wrapper-transitions-down",
            "translate-y-[100%]"
          );
          logoWrapper.classList.add("translate-y-[2px]", "w-[146px]");
        }
      }

      const svg = document.getElementById("hero-logo-svg");
      if (svg) {
        const path = svg.querySelector("path");
        if (path) {
          path.setAttribute(
            "fill",
            value
              ? this.isAtTop && !this.menuOpen
                ? "#fff"
                : "#000"
              : "#000"
          );
        }
      }

      this.$el.dataset.isHomePage = value ? "true" : "false";
    });

    if (this.isHomePage) {
      // Check actual scroll position to determine initial state
      this.isAtTop = window.scrollY === 0;

      // Wait for page to fully load before showing header
      if (document.readyState === "complete") {
        this.showHeader();
      } else {
        window.addEventListener("load", () => {
          this.showHeader();
        });
      }

      // Add scroll listener
      window.addEventListener("scroll", () => {
        this.isAtTop = window.scrollY === 0;
      });

      // Set initial SVG color based on scroll position and menu state
      const svg = document.getElementById("hero-logo-svg");
      if (svg) {
        const path = svg.querySelector("path");
        if (path) {
          path.setAttribute(
            "fill",
            this.isAtTop && !this.menuOpen ? "#fff" : "#000"
          );
        }
      }

      // Ensure scroll position is correctly set after initialization
      this.$nextTick(() => {
        this.isAtTop = window.scrollY === 0;
      });
    } else {
      // For non-home pages, header is already visible (no x-cloak)
      this.isLoaded = true;
    }

    // Make header state globally accessible
    Alpine.store("header", this);

    // Make setScrolledState globally accessible for backward compatibility
    window.setScrolledState = () => this.setScrolledState();

    // Handle SVG path color changes
    this.$watch("isAtTop", (value) => {
      // On non-home pages the logo is always black; nothing to react to.
      if (!this.isHomePage) return;
      const svg = document.getElementById("hero-logo-svg");
      if (svg) {
        const path = svg.querySelector("path");
        if (path) {
          path.setAttribute("fill", value && !this.menuOpen ? "#fff" : "#000");
        }
      }
    });

    // Handle SVG path color changes when menu opens/closes
    this.$watch("menuOpen", (value) => {
      const svg = document.getElementById("hero-logo-svg");
      if (svg) {
        const path = svg.querySelector("path");
        if (path) {
          // On non-homepage, always use black
          if (!this.isHomePage) {
            path.setAttribute("fill", "#000");
          } else {
            path.setAttribute("fill", this.isAtTop && !value ? "#fff" : "#000");
          }
        }
      }
    });
  },

  get headerClasses() {
    if (!this.isHomePage) return "bg-white text-black";
    if (this.menuOpen) return "bg-white text-black";
    return this.isAtTop ? "bg-transparent text-white" : "bg-white text-black";
  },

  get logoClasses() {
    if (!this.isHomePage) return "w-[146px]";
    if (this.isAtTop && !this.menuOpen) {
      return "translate-y-[100%] w-full header-logo-wrapper-transitions-down";
    } else {
      return "translate-y-[2px] w-[146px] header-logo-wrapper-transitions-up";
    }
  },

  setScrolledState() {
    this.isAtTop = false;
    this.applyScrolledTransition();
  },

  applyScrolledTransition() {
    // Let Alpine.js handle the transitions through reactive properties
    // Just ensure the SVG color is set correctly
    const svg = document.getElementById("hero-logo-svg");
    if (svg) {
      const path = svg.querySelector("path");
      if (path) {
        // On non-homepage, always use black
        if (!this.isHomePage) {
          path.setAttribute("fill", "#000");
        } else {
          path.setAttribute(
            "fill",
            this.isAtTop && !this.menuOpen ? "#fff" : "#000"
          );
        }
      }
    }
  },

  showHeader() {
    // Check actual scroll position to determine initial state
    this.isAtTop = window.scrollY === 0;

    // Small delay to ensure smooth transition
    setTimeout(() => {
      this.isLoaded = true;
      this.$el.removeAttribute("x-cloak");

      // Add loaded class to trigger transition
      this.$nextTick(() => {
        this.$el.classList.add("header-loaded");

        // Ensure we're in the correct initial state based on actual scroll
        this.isAtTop = window.scrollY === 0;

        // Apply initial transition classes for smooth appearance
        this.applyInitialTransition();
      });
    }, 100);
  },

  applyInitialTransition() {
    // Let Alpine.js handle the transitions through reactive properties
    // Just ensure the SVG color is set correctly based on scroll position and menu state
    const svg = document.getElementById("hero-logo-svg");
    if (svg) {
      const path = svg.querySelector("path");
      if (path) {
        // On non-homepage, always use black
        if (!this.isHomePage) {
          path.setAttribute("fill", "#000");
        } else {
          path.setAttribute(
            "fill",
            this.isAtTop && !this.menuOpen ? "#fff" : "#000"
          );
        }
      }
    }
  },
});
